import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { MainFacade } from '../../../main.facade';
import { IFileInfo } from '../../../models/file-info';
import { FormBuilder, Validators } from '@angular/forms';
import { RequestFactory } from '../../../models/request.factory';
import {
  CellPoseModel,
  CellPoseModelLabels,
  CellPoseSamModel,
  CellPoseSamModelLabels,
  CellPoseChannelMode,
  ProcessType,
  Request,
} from '../../../models/request';
import { SubmissionStateService } from '../../../../services/submission-state.service';
import { ProcessComponentBase } from '../process.component.base';
import { Subscription } from 'rxjs';
import { VISUALIZER, IVisualizer } from '@jax-data-science/sci-image-visualizer';

/**
 * Unified Cellpose process page.
 *
 * Hosts both v3 (CELLPOSE / `CellPoseRequest`, U-Net family) and v4
 * (CELLPOSE_SAM / `CellPoseSamRequest`, ViT-L Transformer) in a single form.
 * The model dropdown merges {@link CellPoseModel} and the base CPSAM entry of
 * {@link CellPoseSamModel}; selecting CPSAM flips the view into v4 mode,
 * hiding the v3-only tuning knobs and submitting the trimmed v4 request.
 */
@Component({
  selector: 'cellpose',
  templateUrl: './cellpose.component.html',
  styleUrls: ['./cellpose.component.scss'],
})
export class CellposeComponent extends ProcessComponentBase implements OnInit, OnDestroy {
  /**
   * Extensions Cellpose can segment directly. These load fully into RAM, so
   * they must be region-sized — anything bigger goes through a crop step first
   * (see {@link needsCropPrefix} / {@link CELLPOSE_NATIVE_FORMATS}).
   */
  static readonly CELLPOSE_NATIVE_FORMATS = ['tif', 'tiff', 'png', 'jpg', 'jpeg'];

  /**
   * Pyramidal whole-slide formats. Cellpose can't open these and would OOM on
   * the multi-GB pixel data, so they're always routed through a crop step that
   * extracts region-sized tiles first.
   */
  static readonly SLIDE_FORMATS = ['ndpi', 'svs', 'scn', 'mrxs', 'czi', 'qptiff', 'vsi', 'bif'];

  public inputImages: IFileInfo[] = [];

  /**
   * Available pyramid resolution levels of the selected image, populated from
   * the viewer's image metadata (same source the YOLO page uses). Drives the
   * crop-resolution dropdown; level 0 = full resolution.
   */
  public levels = [{ shape: '0: full resolution', level: 0 }];

  private levelSizeSubscription?: Subscription;

  /**
   * Combined model dropdown: every v3 CellPoseModel value plus the v4 CPSAM
   * base. We don't expose the v4 CUSTOM entry here — its enum value collides
   * with v3 CUSTOM (both serialize as the string "CUSTOM"), and a v4 custom
   * finetune is uncommon today; users who need one go through the pipeline.
   */
  readonly modelOptions = [
    {
      label: CellPoseSamModelLabels[CellPoseSamModel.CPSAM],
      value: CellPoseSamModel.CPSAM as string,
    },
    ...Object.values(CellPoseModel).map((m) => ({
      label: CellPoseModelLabels[m],
      value: m as string,
    })),
  ];

  /** Dropdown options for channel mode. */
  readonly channelModeOptions = [
    { label: 'Grayscale', value: CellPoseChannelMode.GRAYSCALE },
    { label: 'Two Channel', value: CellPoseChannelMode.TWO_CHANNEL },
    { label: 'Three Channel', value: CellPoseChannelMode.THREE_CHANNEL },
  ];

  readonly CellPoseModel = CellPoseModel;
  readonly CellPoseSamModel = CellPoseSamModel;
  readonly CellPoseChannelMode = CellPoseChannelMode;

  constructor(
    mainFacade: MainFacade,
    @Inject(VISUALIZER) private plotService: IVisualizer,
    private fb: FormBuilder,
    submissionState: SubmissionStateService,
  ) {
    super('CellPose Segmentation', submissionState, mainFacade);
  }

  ngOnInit(): void {
    this.configForm();

    this.mainFacade.getSelectedNode$().subscribe({
      next: (node) => {
        if (!node?.data) {
          this.bucket = undefined;
        } else {
          this.setImagesInput(node);
        }
      },
      error: (err) => console.error(`Error with node selection: ${err}`),
    });

    this.mainFacade.getValidationMessage$().subscribe({
      next: (message) => {
        this.validationMessage = message;
      },
    });

    // Populate the crop-resolution dropdown with the slide's available pyramid
    // levels (mirrors the YOLO page). Level 0 = full resolution.
    this.levelSizeSubscription = this.plotService.getImageMeta().subscribe((imageMeta) => {
      if (!imageMeta || imageMeta.length === 0) {
        this.levels = [{ shape: '0: full resolution', level: 0 }];
        return;
      }
      this.levels = imageMeta.map((m, i) => ({ shape: `${i}: ${m.x}x${m.y}`, level: i }));
    });
  }

  ngOnDestroy(): void {
    this.levelSizeSubscription?.unsubscribe();
  }

  setImagesInput(node: TreeNode<IFileInfo>): void {
    this.inputImages = [];
    if (node?.children && node.children.length > 0) {
      node.children.forEach((child) => {
        if (child.data && this.isImageExtension(child.data.name)) {
          this.inputImages.push(child.data);
        }
      });
    } else if (node?.children?.length === 0) {
      if (node.data && this.isImageExtension(node.data.name)) {
        this.inputImages.push(node.data);
      }
    }
    this.bucket = node.data?.project.bucket;
    this.path = node.leaf ? node.parent?.data?.relPath : node.data?.relPath;
  }

  isImageExtension(name: string): boolean {
    const ext = name.toLowerCase().split('.').pop() ?? '';
    return (
      CellposeComponent.CELLPOSE_NATIVE_FORMATS.includes(ext) || CellposeComponent.SLIDE_FORMATS.includes(ext)
    );
  }

  /** True for pyramidal slide formats Cellpose cannot read directly. */
  isSlideFormat(name: string): boolean {
    const ext = name.toLowerCase().split('.').pop() ?? '';
    return CellposeComponent.SLIDE_FORMATS.includes(ext);
  }

  /**
   * Whether this submission must be prepended with a CROP step. True when the
   * selected file is a whole-slide format (Cellpose can't read it) or the user
   * has drawn regions (segment only those). Otherwise Cellpose runs directly.
   */
  needsCropPrefix(): boolean {
    const name = this.mainFacade.getSelectedFileInfo()?.name;
    const slide = !!name && this.isSlideFormat(name);
    const diagram = this.mainFacade.getDiagram();
    const hasRegions = !!diagram && typeof diagram.hasRegions === 'function' && diagram.hasRegions();
    return slide || hasRegions;
  }

  configForm(): void {
    // Single form holding both the v3 and v4 surfaces. v3-only fields keep
    // their defaults when CPSAM is selected — they're ignored at submit
    // time by RequestFactory.getCellPoseSamRequest, which only reads the
    // v4-relevant fields.
    this.form = this.fb.group({
      model: [CellPoseSamModel.CPSAM as string, Validators.required],
      modelLocationPath: [null],
      channelMode: [CellPoseChannelMode.GRAYSCALE, Validators.required],
      cytoChannel: [1, Validators.required],
      nucChannel: [2, Validators.required],
      diameter: [0.0, Validators.required],
      // Common to both v3 and v4:
      cellprobThreshold: [0.0, Validators.required],
      // v3-only fields below — hidden in the template when isCpsamModel:
      flowThreshold: [0.4, Validators.required],
      minSize: [15, Validators.required],
      do3D: [false],
      normalize: [true],
      invert: [false],
      augment: [false],
      resample: [true],
      tileOverlap: [0.1, Validators.required],
      batchSize: [8, Validators.required],
      stitchThreshold: [0.0, Validators.required],
      // Pyramid level for the auto-prepended crop step (0 = full resolution).
      // Only consulted when needsCropPrefix() is true.
      cropResolution: [0, Validators.required],
      // Also save the instance-label mask as {basename}_mask.png (v3 + v4).
      saveMaskPng: [false],
      outputFolder: [''],
      editOutputFolder: [false],
    });
    this.persistForm();
  }

  /**
   * True when the user has picked the Cellpose-SAM (v4) model. Drives the
   * template's *ngIf branching and the request-type choice on submit.
   */
  get isCpsamModel(): boolean {
    return this.form.get('model')?.value === CellPoseSamModel.CPSAM;
  }

  /**
   * True when the user picked v3 CUSTOM and needs to supply a model path.
   * Only the v3 family exposes CUSTOM in this UI — see modelOptions.
   */
  get isCustomModel(): boolean {
    return this.form.get('model')?.value === CellPoseModel.CUSTOM;
  }

  get isMultiChannel(): boolean {
    const mode = this.form.get('channelMode')?.value;
    return mode === CellPoseChannelMode.TWO_CHANNEL || mode === CellPoseChannelMode.THREE_CHANNEL;
  }

  /**
   * Build the appropriate request type for the picked model and submit.
   * CPSAM → CellPoseSamRequest (processType = CELLPOSE_SAM, routes to the
   * jit-cellpose-sam-worker queue). Anything else → CellPoseRequest
   * (processType = CELLPOSE, routes to the legacy jit-cellpose worker).
   * Routing happens in MainFacade.hackRequest via request.processType.
   */
  onSubmit(): void {
    const cropPrefix = this.needsCropPrefix();
    if (this.isCpsamModel) {
      const request = RequestFactory.getCellPoseSamRequest(
        this.form.value,
        this.bucket,
        this.path,
        this.inputImages,
      );
      if (cropPrefix) {
        this.submitWithCrop(request, ProcessType.CELLPOSE_SAM);
        return;
      }
      super.submit(request);
      return;
    }
    const request = RequestFactory.getCellPoseRequest(
      this.form.value,
      this.bucket,
      this.path,
      this.inputImages,
    );
    if (cropPrefix) {
      this.submitWithCrop(request, ProcessType.CELLPOSE);
      return;
    }
    super.submit(request);
  }

  /**
   * A Rule submits a plain single-type request per discovered file (see
   * RuleTriggerService, jit-parent) - it has no equivalent of the
   * CROP→CELLPOSE COMPOUND chain `submitWithCrop` builds, so Create Rule is
   * only offered when the run wouldn't need that crop step. The template
   * disables the button in that case with an explanatory tooltip.
   */
  onCreateRule(): void {
    if (this.needsCropPrefix()) return;
    const request = this.isCpsamModel
      ? RequestFactory.getCellPoseSamRequest(this.form.value, this.bucket, this.path, this.inputImages)
      : RequestFactory.getCellPoseRequest(this.form.value, this.bucket, this.path, this.inputImages);
    super.createRule(request);
  }

  /**
   * Submit a whole-slide / region run as a CROP→CELLPOSE COMPOUND. The crop
   * step extracts region-sized tiles (drawn regions, or auto-found tissue) that
   * Cellpose can actually load; the server chains crop output into Cellpose's
   * inputPaths and fans out one Cellpose run per region.
   */
  private submitWithCrop(request: Request, cellProcessType: ProcessType): void {
    this.isSubmitting = true;
    const cropResolution = this.form.get('cropResolution')?.value ?? 0;
    this.mainFacade.runCellPoseWithCrop(request, cellProcessType, cropResolution).subscribe({
      next: () => {
        this.isSubmitting = false;
      },
      error: () => {
        this.isSubmitting = false;
      },
    });
  }
}
